/* eslint-disable max-len */
import { Calendar } from "@react-admin/ra-calendar";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { IShowAppointmentDialogData } from "./CustomAppointmentList";
import { IShowAppointmentServiceDialogData } from "./AppointmentServiceList";
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Theme,
  useMediaQuery
} from "@mui/material";
import CalendarDatesSelect from "./CalendarDatesSelect";
import * as User from "providers/user";
import { ArrowLeftIcon, ArrowRightIcon, FilterIcon } from "assets/Icons";
import {
  FilterButton,
  FilterForm,
  ReferenceInput,
  SelectInput,
  useDeleteMany,
  useListContext,
  useTranslate,
  useUpdate
} from "react-admin";
import { eventTextColors } from "./AvailabilityList";
import MobileFilterModal from "./MobileFilterModal";
import { useQuery } from "@apollo/client";
import { GET_LOCATIONS } from "gql";

interface CustomCalendarProps {
  locale: string;
  convertToEvent: any;
  type: string;
  artistFilter: string;
  select?: (arg: any) => void;
  editable?: boolean;
  setEditDialogId?: Dispatch<SetStateAction<string>>;
  isPopoverOpen?: boolean;
  handlePopoverOpen?: (note: string) => void;
  handlePopoverClose?: () => void;
  anchorEl?: HTMLElement | null;
  setShowDialogData?: Dispatch<
    SetStateAction<
      IShowAppointmentDialogData | IShowAppointmentServiceDialogData | null
    >
  >;
  selectable?: boolean;
  pageTitle?: string;
  setSelectedDate?: Dispatch<SetStateAction<{ start: Date; end: Date } | null>>;
  selectedView?: string;
  setSelectedView?: Dispatch<SetStateAction<string>>;
  calendarsDate?: Dayjs | null;
  setCalendarsDate?: Dispatch<SetStateAction<Dayjs | null>>;
  setSelectedCalendarDate?: Dispatch<SetStateAction<any>>;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

const CustomCalendar = ({
  locale,
  convertToEvent,
  type,
  artistFilter,
  select,
  editable,
  setEditDialogId,
  isPopoverOpen,
  handlePopoverOpen,
  handlePopoverClose,
  anchorEl,
  setShowDialogData,
  selectable,
  pageTitle,
  setSelectedDate,
  selectedView,
  setSelectedView,
  calendarsDate,
  setCalendarsDate,
  setSelectedCalendarDate
}: CustomCalendarProps) => {
  const translate = useTranslate();
  let calRef = useRef<any>();
  const { user, permissions } = User.useUser();
  const { filterValues } = useListContext();
  const [isPrev, setIsPrev] = useState<boolean>(false);
  const [isNext, setIsNext] = useState<boolean>(false);
  const [deleteMany] = useDeleteMany();
  const [edit] = useUpdate();

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  useEffect(() => {
    if (calRef.current) {
      if (setSelectedCalendarDate) {
        setSelectedCalendarDate({
          title: calRef.current.currentDataManager?.data?.viewTitle,
          dateForFilter: JSON.parse(
            JSON.stringify(
              dayjs(calRef.current.currentDataManager?.data?.currentDate)
            )
          )
        });
      }
      if (isPrev) {
        calRef?.current?.prev();
        localStorage.setItem(
          "ServiceCalendarDateFilter",
          JSON.stringify(
            dayjs(calRef.current.currentDataManager?.data?.currentDate)
          )
        );
      }
      if (isNext) {
        calRef?.current?.next();
        localStorage.setItem(
          "ServiceCalendarDateFilter",
          JSON.stringify(
            dayjs(calRef.current.currentDataManager?.data?.currentDate)
          )
        );
      }
      if (calendarsDate) {
        calRef.current.gotoDate(calendarsDate.format());
        if (setCalendarsDate) {
          setCalendarsDate(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calRef, isPrev, isNext, calendarsDate, setSelectedCalendarDate]);

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const editWithDrag = (eventChangeInfo: any) => {
    const eventData = eventChangeInfo.event;

    const artists = eventData.extendedProps.artistsIdsArray?.map(
      (artistId: string) => ({
        artist: { id: artistId }
      })
    );

    const locationId = eventData.extendedProps.locationId;

    const locationTimezone =
      locationsData.find((loc) => loc.id === locationId)?.timezone || "UTC";

    const userId = user?.impersonate_id || user?.id;

    const eventStart = eventData.start;
    const eventEnd = eventData.end;

    const startInLocationTz = dayjs(eventStart).tz(locationTimezone, true);
    const endInLocationTz = dayjs(eventEnd).tz(locationTimezone, true);

    const start_time = startInLocationTz.toISOString();
    const end_time = endInLocationTz.toISOString();

    const retval: any = {
      inserted_by: {
        id: userId
      },
      location: {
        id: String(locationId)
      },
      start_time,
      end_time,
      status: "confirmed",
      artists
    };

    if (eventData.extendedProps.recurring) {
      retval.recurring = false;
      retval.group_availability = {};
    }

    deleteMany("ArtistAvailability", {
      ids: eventData.extendedProps.artistsAvailabilityIdsArray || []
    });

    edit(
      "Availability",
      {
        id: eventData.id,
        data: retval
      },
      {
        onSuccess: () => {
          // TODO: show success message.
        },
        onError: (error) => {
          // TODO: show error message
        }
      }
    );
  };

  const customFilters = [
    <LocationFilter
      source="location_id"
      label="Location"
      locationIds={locationIds}
    />
  ];

  const customMobileFilters = [
    <LocationFilter
      source="location_id"
      label="Location"
      locationIds={locationIds}
      alwaysOn={true}
    />
  ];

  const isSmall = useMediaQuery<Theme>(
    type === "availability" ? "(max-width:915px)" : "(max-width:640px)"
  );
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [isLocationMobileFilterModalOpen, setIsLocationMobileFilterModalOpen] =
    useState<boolean>(false);

  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };

  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen ? "simple-date-popover" : undefined;

  const selectedLocation = [
    ...(user?.joined_company?.locations || []),
    ...(user?.company?.locations || [])
  ]?.find((item) => item.id === filterValues?.location_id);

  const businessHours = selectedLocation && [
    {
      daysOfWeek: [0],
      startTime: dayjs(selectedLocation?.sunday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.sunday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [1],
      startTime: dayjs(selectedLocation?.monday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.monday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [2],
      startTime: dayjs(selectedLocation?.tuesday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.tuesday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [3],
      startTime: dayjs(selectedLocation?.wednesday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.wednesday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [4],
      startTime: dayjs(selectedLocation?.thursday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.thursday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [5],
      startTime: dayjs(selectedLocation?.friday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.friday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [6],
      startTime: dayjs(selectedLocation?.saturday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.saturday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    }
  ];

  useEffect(() => {
    setSelectedDate &&
      setSelectedDate(
        calRef.current?.currentDataManager?.data?.dateProfile?.activeRange
      );
  }, [
    setSelectedDate,
    calRef.current?.currentDataManager?.data?.dateProfile?.activeRange
  ]);

  return (
    <div>
      {pageTitle && (
        <div
          className="absolute left-0 flex font-['OptimaLTStd'] font-[600] text-[24px] text-mono-primaryTextColor"
          style={{ top: user.impersonate_company_id ? "49px" : "9px" }}
        >
          {!isSmall && pageTitle}
          {!isSmall && type === "availability" && (
            <Box
              sx={{
                "& .MuiTextField-root": {
                  minWidth: "130px",
                  maxWidth: "160px"
                }
              }}
              className="mt-[-16px] ml-[8px]"
            >
              <FilterForm filters={customFilters} />
            </Box>
          )}
        </div>
      )}
      <Box
        className="absolute flex gap-[8px]"
        guide-id={
          type === "availability"
            ? "availability-date-and-location-filters"
            : undefined
        }
        sx={{
          top: user.impersonate_company_id ? "49px" : "9px",
          right:
            window.location.pathname === "/dashboard/Services-calendar" ||
            window.location.pathname === "/dashboard"
              ? "16px"
              : "0px",
          "@media screen and (max-width:430px)": {
            right: "8px"
          }
        }}
      >
        {type === "availability" && (
          <div className="customListActionsButtonsWrapper">
            {isSmall ? (
              <div>
                <IconButton
                  className="h-[36px] w-[36px]"
                  onClick={() => setIsLocationMobileFilterModalOpen(true)}
                >
                  <Badge
                    color="error"
                    badgeContent={!!filterValues?.location_id ? 1 : 0}
                  >
                    <FilterIcon />
                  </Badge>
                </IconButton>
                {isLocationMobileFilterModalOpen && (
                  <MobileFilterModal
                    open={isLocationMobileFilterModalOpen}
                    onClose={() => setIsLocationMobileFilterModalOpen(false)}
                    title="Filter"
                  >
                    <div className="flex flex-col items-center">
                      <FilterForm filters={customMobileFilters} />
                    </div>
                  </MobileFilterModal>
                )}
              </div>
            ) : (
              <FilterButton filters={customFilters} disableSaveQuery />
            )}
          </div>
        )}
        <div className="flex border border-[rgba(48,47,46,0.23)] bg-[#FAF9F7] rounded-[32px]">
          <IconButton
            sx={{
              borderRadius: "0px",
              borderTopLeftRadius: "32px",
              borderBottomLeftRadius: "32px"
            }}
            className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
            onClick={() => {
              setIsPrev(true);
              setTimeout(() => {
                setIsPrev(false);
              }, 0);
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
          <button
            className={`border-none h-[36px] flex justify-center items-center font-['Inter']font-[500] 
            text-mono-primaryTextColor hover:bg-[rgba(0,0,0,0.04)] ${
              isSmall ? "px-[6px] text-[12px]" : "px-[12px] text-[14px]"
            }`}
            onClick={handleDatePopoverClick}
          >
            {calRef?.current?.currentDataManager?.data?.viewTitle}
          </button>
          <IconButton
            sx={{
              borderRadius: "0px",
              borderTopRightRadius: "32px",
              borderBottomRightRadius: "32px"
            }}
            className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
            onClick={() => {
              setIsNext(true);
              setTimeout(() => {
                setIsNext(false);
              }, 0);
            }}
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      </Box>
      <Calendar
        locale={locale}
        convertToEvent={convertToEvent}
        datesSet={(dateInfo) => (calRef.current = dateInfo?.view?.calendar)}
        select={select}
        selectable={selectable}
        businessHours={businessHours}
        eventChange={(eventChangeInfo) => {
          editWithDrag(eventChangeInfo);
        }}
        eventClassNames={(args) => {
          return args?.event?._def?.extendedProps?.artistsIds?.includes(
            artistFilter
          )
            ? ""
            : "event-hidden";
        }}
        eventContent={(e: any): JSX.Element => {
          if (type === "availability") {
            return (
              <div
                id="customEventWrapper"
                title={
                  e?.event?._def?.title +
                  " - " +
                  e?.event?._def?.extendedProps?.time
                }
              >
                <p id="customEventTitle">{e?.event?._def?.title}</p>
                <p id="customEventTime">
                  {e?.event?._def?.extendedProps?.time}
                </p>
              </div>
            );
          }
          if (type === "appointment") {
            return (
              <>
                {e?.event?._def?.extendedProps?.notes ? (
                  <>
                    <div
                      id="customEventWrapper"
                      title={
                        e?.event?._def?.extendedProps?.serviceName +
                        `${
                          e?.event?._def?.extendedProps?.serviceName2
                            ? " & " +
                              e?.event?._def?.extendedProps?.serviceName2
                            : ""
                        }` +
                        " - " +
                        e?.event?._def?.title +
                        " - " +
                        e?.event?._def?.extendedProps?.time
                      }
                    >
                      {e?.event?._def?.extendedProps?.groupBooking && (
                        <p id="customEventGroupBooking">Group Booking</p>
                      )}
                      <p id="customEventTime">
                        {e?.event?._def?.extendedProps?.serviceName}
                        {e?.event?._def?.extendedProps?.serviceName2
                          ? ` & ${e.event._def.extendedProps.serviceName2}`
                          : ""}
                      </p>
                      <p id="customEventTitle">{e?.event?._def?.title}</p>
                      <p id="customEventTime">
                        {e?.event?._def?.extendedProps?.time}
                      </p>
                    </div>
                    <div
                      id="customEventNote"
                      style={{
                        backgroundColor:
                          e?.event?._def?.extendedProps?.duration > 0 &&
                          e?.event?._def?.extendedProps?.duration < 30
                            ? "#FFB469"
                            : e?.event?._def?.extendedProps?.duration > 30 &&
                              e?.event?._def?.extendedProps?.duration < 61
                            ? "#93E1F4"
                            : "#F99C75"
                      }}
                      aria-owns={
                        isPopoverOpen ? "mouse-over-popover" : undefined
                      }
                      aria-haspopup="true"
                      onMouseEnter={() =>
                        handlePopoverOpen &&
                        handlePopoverOpen(e?.event?._def?.extendedProps?.notes)
                      }
                      onMouseLeave={handlePopoverClose}
                    />
                  </>
                ) : (
                  <div
                    id="customEventWrapper"
                    title={
                      e?.event?._def?.extendedProps?.serviceName +
                      `${
                        e?.event?._def?.extendedProps?.serviceName2
                          ? " & " + e?.event?._def?.extendedProps?.serviceName2
                          : ""
                      }` +
                      " - " +
                      e?.event?._def?.title +
                      " - " +
                      e?.event?._def?.extendedProps?.time
                    }
                  >
                    {e?.event?._def?.extendedProps?.groupBooking && (
                      <p id="customEventGroupBooking">Group Booking</p>
                    )}
                    <p id="customEventTime">
                      {e?.event?._def?.extendedProps?.serviceName}
                      {e?.event?._def?.extendedProps?.serviceName2
                        ? ` & ${e.event._def.extendedProps.serviceName2}`
                        : ""}
                    </p>
                    <p id="customEventTitle">{e?.event?._def?.title}</p>
                    <p id="customEventTime">
                      {e?.event?._def?.extendedProps?.time}
                    </p>
                  </div>
                )}
              </>
            );
          }
          if (type === "appointmentService") {
            return (
              <>
                {e?.event?._def?.extendedProps?.appointmentNotes ? (
                  <>
                    <div
                      id="customEventWrapper"
                      title={
                        e?.event?._def?.extendedProps?.isBlockedTime
                          ? `${translate("appointment.blocked_time")} - ${
                              e?.event?._def?.extendedProps?.time
                            }`
                          : e?.event?._def?.extendedProps?.serviceName +
                            (!!e?.event?._def?.extendedProps?.addOns?.length
                              ? ` (${e?.event?._def?.extendedProps?.addOns
                                  ?.map((item: any) => item.core_service.name)
                                  ?.join(", ")})`
                              : "") +
                            ` - ${
                              e?.event?._def?.extendedProps?.guestName
                                ? e?.event?._def?.extendedProps?.guestName
                                : e?.event?._def?.title
                            }` +
                            " - " +
                            e?.event?._def?.extendedProps?.time
                      }
                    >
                      <p
                        id="customEventTime"
                        className={
                          e?.event?._def?.extendedProps?.duration > 30
                            ? "line-clamp-2"
                            : "line-clamp-1"
                        }
                      >
                        {e?.event?._def?.extendedProps?.isBlockedTime
                          ? ""
                          : e?.event?._def?.extendedProps?.serviceName}
                        {e?.event?._def?.extendedProps?.isBlockedTime
                          ? ""
                          : !!e?.event?._def?.extendedProps?.addOns?.length
                          ? ` (${e?.event?._def?.extendedProps?.addOns
                              ?.map((item: any) => item.core_service.name)
                              ?.join(", ")})`
                          : ""}
                      </p>
                      <p id="customEventTitle">
                        {e?.event?._def?.extendedProps?.isBlockedTime
                          ? translate("appointment.blocked_time")
                          : e?.event?._def?.extendedProps?.guestName
                          ? e?.event?._def?.extendedProps?.guestName
                          : e?.event?._def?.title}
                      </p>
                      <p id="customEventTime">
                        {e?.event?._def?.extendedProps?.time}
                      </p>
                    </div>
                    <div
                      id="customEventNote"
                      style={{
                        backgroundColor:
                          e?.event?._def?.extendedProps?.duration > 0 &&
                          e?.event?._def?.extendedProps?.duration < 30
                            ? "#FFB469"
                            : e?.event?._def?.extendedProps?.duration > 30 &&
                              e?.event?._def?.extendedProps?.duration < 61
                            ? "#93E1F4"
                            : "#F99C75"
                      }}
                      aria-owns={
                        isPopoverOpen ? "mouse-over-popover" : undefined
                      }
                      aria-haspopup="true"
                      onMouseEnter={() =>
                        handlePopoverOpen &&
                        handlePopoverOpen(
                          e?.event?._def?.extendedProps?.appointmentNotes
                        )
                      }
                      onMouseLeave={handlePopoverClose}
                    />
                  </>
                ) : (
                  <div
                    id="customEventWrapper"
                    title={
                      e?.event?._def?.extendedProps?.isBlockedTime
                        ? `${translate("appointment.blocked_time")} - ${
                            e?.event?._def?.extendedProps?.time
                          }`
                        : e?.event?._def?.extendedProps?.serviceName +
                          (!!e?.event?._def?.extendedProps?.addOns?.length
                            ? ` (${e?.event?._def?.extendedProps?.addOns
                                ?.map((item: any) => item.core_service.name)
                                ?.join(", ")})`
                            : "") +
                          ` - ${
                            e?.event?._def?.extendedProps?.guestName
                              ? e?.event?._def?.extendedProps?.guestName
                              : e?.event?._def?.title
                          }` +
                          " - " +
                          e?.event?._def?.extendedProps?.time
                    }
                  >
                    <p
                      id="customEventTime"
                      className={
                        e?.event?._def?.extendedProps?.duration > 30
                          ? "line-clamp-2"
                          : "line-clamp-1"
                      }
                    >
                      {e?.event?._def?.extendedProps?.isBlockedTime
                        ? ""
                        : e?.event?._def?.extendedProps?.serviceName}
                      {e?.event?._def?.extendedProps?.isBlockedTime
                        ? ""
                        : !!e?.event?._def?.extendedProps?.addOns?.length
                        ? ` (${e?.event?._def?.extendedProps?.addOns
                            ?.map((item: any) => item.core_service.name)
                            ?.join(", ")})`
                        : ""}
                    </p>
                    <p id="customEventTitle">
                      {e?.event?._def?.extendedProps?.isBlockedTime
                        ? translate("appointment.blocked_time")
                        : e?.event?._def?.extendedProps?.guestName
                        ? e?.event?._def?.extendedProps?.guestName
                        : e?.event?._def?.title}
                    </p>
                    <p id="customEventTime">
                      {e?.event?._def?.extendedProps?.time}
                    </p>
                  </div>
                )}
              </>
            );
          }
          return <div />;
        }}
        editable={editable}
        mutationMode="optimistic"
        height={isSmall ? "calc(100vh - 1em - 64px)" : "calc(100vh - 1em)"}
        // headerToolbar={{
        //   left: "",
        //   center: "",
        //   right: ""
        // }}
        headerToolbar={{
          left: "",
          center: "",
          right: "dayGridMonth timeGridWeek timeGridDay"
        }}
        initialView={
          selectedView && selectedView !== "day" ? selectedView : "timeGridWeek"
        }
        nowIndicator={true}
        eventClick={(e) => {
          if (type === "availability") {
            e?.event?._def?.publicId &&
              setEditDialogId &&
              setEditDialogId(e.event._def.publicId);
          }
          if (type === "appointment" && setShowDialogData) {
            if (anchorEl) return;
            setShowDialogData({
              id: e?.event?._def?.publicId,
              client: e?.event?._def?.title,
              client2: e?.event?._def?.extendedProps?.client2Name,
              time: e?.event?._def?.extendedProps?.time,
              artist: e?.event?._def?.extendedProps?.artistFullName,
              artist2: e?.event?._def?.extendedProps?.artist2FullName,
              serviceName: e?.event?._def?.extendedProps?.serviceName,
              serviceName2: e?.event?._def?.extendedProps?.serviceName2,
              date: dayjs(e?.event?._def?.extendedProps?.start_time).format(
                "D MMMM YYYY (dddd)"
              ),
              artistPhoto: e?.event?._def?.extendedProps.artistPhoto,
              artist2Photo: e?.event?._def?.extendedProps.artist2Photo,
              clientPhoto: e?.event?._def?.extendedProps.clientPhoto,
              note: e?.event?._def?.extendedProps.notes,
              serviceDuration: e?.event?._def?.extendedProps?.serviceDuration,
              serviceDuration2: e?.event?._def?.extendedProps?.serviceDuration2,
              servicePrice: e?.event?._def?.extendedProps?.servicePrice,
              servicePrice2: e?.event?._def?.extendedProps?.servicePrice2,
              amountPaid: e?.event?._def?.extendedProps?.amountPaid,
              groupBooking: e?.event?._def?.extendedProps?.groupBooking,
              locationName: e?.event?._def?.extendedProps?.locationName
            });
          }
          if (type === "appointmentService" && setShowDialogData) {
            if (anchorEl) return;
            setShowDialogData({
              id: e?.event?._def?.publicId,
              client: e?.event?._def?.title,
              time: e?.event?._def?.extendedProps?.time,
              artistId: e?.event?._def?.extendedProps?.artistId,
              artist: e?.event?._def?.extendedProps?.artistFullName,
              serviceName: e?.event?._def?.extendedProps?.serviceName,
              date: dayjs(e?.event?._def?.extendedProps?.start_time).format(
                "D MMMM YYYY (dddd)"
              ),
              artistPhoto: e?.event?._def?.extendedProps.artistPhoto,
              clientPhoto: e?.event?._def?.extendedProps.clientPhoto,
              guestName: e?.event?._def?.extendedProps.guestName,
              appointmentId: e?.event?._def?.extendedProps.appointmentId,
              appointmentNotes: e?.event?._def?.extendedProps.appointmentNotes,
              serviceDuration: e?.event?._def?.extendedProps?.duration,
              servicePrice: e?.event?._def?.extendedProps?.servicePrice,
              locationName: e?.event?._def?.extendedProps?.locationName,
              addOns: e?.event?._def?.extendedProps?.addOns,
              startDateAndTime: e?.event?._def?.extendedProps?.startDateAndTime,
              locationTimezone: e?.event?._def?.extendedProps?.locationTimezone,
              isBlockedTime: e?.event?._def?.extendedProps?.isBlockedTime,
              clientPhoneNumber:
                e?.event?._def?.extendedProps?.clientPhoneNumber
            });
          }
        }}
        slotDuration="00:15:00"
        slotLabelInterval="01:00"
        sx={{
          "& .fc-toolbar.fc-header-toolbar": {
            marginBottom: "55px",
            maxHeight: user.impersonate_company_id ? "94px" : "54px",
            minHeight: user.impersonate_company_id ? "94px" : "54px"
          },
          "& .fc-daygrid-body-natural": {
            display: "none" // remove all day section row
          },
          "& .fc-timegrid-divider": {
            display: "none" // remove short empty space below all day section row
          },
          "& .fc-toolbar-title": {
            color: "rgba(48, 47, 46, 0.6)",
            fontSize: "18px",
            lineHeight: "31px",
            verticalAlign: "middle",
            fontWeight: "500",
            fontFamily: "Inter",
            textTransform: "uppercase",
            display: "inline-block"
          },
          "& .fc .fc-scrollgrid-section-liquid > td": {
            borderBottomRightRadius: "12px",
            borderBottomLeftRadius: "12px"
          },
          "& .fc-timegrid-slot-label-cushion": {
            color: "rgba(48, 47, 46, 0.6)",
            fontSize: "12px",
            fontWeight: "400",
            fontFamily: "Inter",
            textTransform: "uppercase"
          },
          "& .fc-daygrid-day-number": {
            color: "rgba(48, 47, 46, 0.6)",
            fontSize: "18px",
            fontWeight: "400",
            fontFamily: "Inter"
          },
          "& .fc-col-header-cell-cushion": {
            color: "rgba(48, 47, 46, 0.6)",
            fontSize: "12px",
            fontWeight: "400",
            fontFamily: "Inter",
            textTransform: "uppercase"
          },
          "& .fc-direction-ltr .fc-timegrid-slot-label-frame": {
            textAlign: "center"
          },
          "& .fc-button": {
            display: "none",
            backgroundColor: "#F6F4F1",
            color: "rgba(48, 47, 46, 0.95)",
            borderColor: "#ddd !important",
            fontSize: "13px",
            fontWeight: 400,
            textTransform: "capitalize",
            borderRadius: "24px",
            lineHeight: "18px",
            fontFamily: "Inter"
          },
          "& .fc-button-primary:disabled": {
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.07)",
              color: "rgba(0, 0, 0, 0.54)"
            }
          },
          "& .fc-button:hover": {
            borderColor: "#ddd !important",
            backgroundColor: "rgba(184, 114, 42, 0.07)",
            color: "rgba(48, 47, 46, 0.95)"
          },
          "& .fc-button-primary:not(:disabled):active": {
            backgroundColor: "rgba(184, 114, 42, 0.1)",
            color: "rgba(48, 47, 46, 0.95)"
          },
          "& .fc-button-primary:not(:disabled).fc-button-active": {
            backgroundColor: "#B8722A",
            "&:hover": {
              backgroundColor: "#B8722A"
            },
            color: "#ffffff",
            borderColor: "#B8722A !important"
          },
          "& .fc-today-button": {
            marginLeft: "0px !important"
          },
          "& .fc-scrollgrid-liquid": {
            borderRadius: "12px"
          },
          "& .fc-theme-standard th": {
            borderTopRightRadius: "12px"
          },
          "& .fc-scroller": {
            overflow: "hidden auto !important"
          },
          "& .fc-day-today": {
            backgroundColor: "rgba(184, 114, 42, 0.03) !important",
            borderRadius: "0px !important"
          },
          "& .fc-prev-button": {
            position: "absolute",
            top: "109px",
            left: "0px",
            padding: "5px 10px 5px 11px",
            zIndex: 10,
            borderColor: "#ddd !important",
            borderBottom: "none",
            borderRight: "none",
            borderRadius: 0,
            borderTopLeftRadius: "12px",
            backgroundColor: "transparent",
            color: "rgba(48, 47, 46, 0.38)"
          },
          "& .fc-next-button": {
            position: "absolute",
            top: "109px",
            right: "0px",
            padding: "5px 10px 5px 11px",
            zIndex: 10,
            borderColor: "#ddd !important",
            borderBottom: "none",
            borderLeft: "none",
            borderRadius: 0,
            borderTopRightRadius: "12px",
            backgroundColor: "transparent",
            color: "rgba(48, 47, 46, 0.38)"
          },
          "& .fc-timegrid-event-harness-inset .fc-timegrid-event, .fc-timegrid-event.fc-event-mirror, .fc-timegrid-more-link":
            {
              boxShadow: "0px 0px 0px 1px #F6F4F1",
              border: "none",
              borderRadius: "8px",
              padding: "4px"
            },
          "& .fc-theme-standard .fc-popover": {
            backgroundColor: "#ffffff !important"
          },
          "& #customEventWrapper": {
            position: "sticky",
            top: "0px",
            bottom: "0px",
            maxHeight: "100%",
            overflow: "hidden"
          },
          "& #customEventTitle": {
            fontSize: "15px",
            fontWeight: 600,
            fontFamily: "Inter",
            lineHeight: "18px"
          },
          "& #customEventGroupBooking": {
            fontSize: "15px",
            fontWeight: 600,
            fontFamily: "Inter",
            lineHeight: "18px",
            textTransform: "uppercase"
          },
          "& #customEventTime": {
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: "Inter",
            lineHeight: "14px"
          },
          "& #customEventNote": {
            height: "10px",
            width: "10px",
            position: "absolute",
            top: "-6px",
            left: "-5px",
            zIndex: "inherit"
          },
          "& .fc-daygrid-more-link": {
            fontFamily: "Inter"
          },
          "& .fc-highlight": {},
          "& .event-hidden": {
            display: "none"
          }
        }}
      />
      {calRef?.current && (
        <Popover
          id={datePopoverId}
          open={datePopoverOpen}
          anchorEl={dateAnchorEl}
          onClose={handleDatePopoverClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: -25,
            horizontal: "center"
          }}
        >
          <CalendarDatesSelect
            calendarApi={calRef.current}
            handleDatePopoverClose={handleDatePopoverClose}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setCalendarsDate={setCalendarsDate}
            localStorageKey="ServiceCalendarDateFilter"
          />
        </Popover>
      )}
    </div>
  );
};

export default CustomCalendar;

const LocationFilter = ({ source, label, locationIds, alwaysOn }: any) => {
  const { permissions } = User.useUser();
  return (
    <ReferenceInput
      source={source}
      reference="Location"
      label={label}
      filter={{ company_id: permissions.companyId }}
    >
      <SelectInput
        optionText={(value) => (
          <div
            style={{
              color: eventTextColors?.[locationIds?.indexOf(value?.id)],
              overflow: "hidden"
            }}
          >
            {value.nickname || value.name}
          </div>
        )}
        format={(value) => (!value ? null : value)}
        emptyText={alwaysOn ? "All Locations" : ""}
      />
    </ReferenceInput>
  );
};
